import { Activity, Suspense, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate, useOutlet, UNSAFE_LocationContext, type Location } from 'react-router';
import { House, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { useMarket } from '@/app/market';
import { routeResources, type FeatureRoute } from '@/app/registry';
import { sections } from '@/app/sections';
import { useNavTitle } from '@/i18n/useNavTitle';
import { cn, focusRing, PageSpinner, useConfirm } from '@/ui';
import type { ShellContext } from './shellContext';
import { WorkspaceContext, WorkspacePageContext } from './workspaceContext';

const routes = sections.flatMap((section) => section.routes);
const homeLocation: Location = { pathname: '/', search: '', hash: '', state: null, key: 'workspace-home' };

interface Page {
  path: string;
  location: Location;
  locationContext: React.ContextType<typeof UNSAFE_LocationContext>;
  outlet: ReactNode;
}

function routeFor(path: string): FeatureRoute | undefined {
  return routes.find((route) => matchPath({ path: route.path, end: true }, path));
}

function url(location: Location) {
  return location.pathname + location.search + location.hash;
}

interface WorkspaceState {
  pages: Page[];
  active: string;
  marketKey: string;
  fallback: ReactNode;
  dirty: Record<string, boolean>;
  setDirty(path: string, dirty: boolean): void;
  close(path: string): Promise<void>;
}
const PagesContext = createContext<WorkspaceState | null>(null);

/** Keeps one mounted page per pathname; query changes update that page instead of opening tabs. */
export function WorkspaceProvider({ children, outletContext }: { children: ReactNode; outletContext?: ShellContext }) {
  const location = useLocation();
  const locationContext = useContext(UNSAFE_LocationContext);
  const outlet = useOutlet(outletContext);
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { areaId } = useMarket();
  const { t } = useTranslation('shell');
  const confirm = useConfirm();
  const [dirty, updateDirty] = useState<Record<string, boolean>>({});
  const marketKey = String(areaId ?? 'all');
  const canOpen = (path: string) => path === '/' || path === '/profile' || (routeFor(path) && permissions.canViewAny(routeResources(routeFor(path)!)));
  const home = (): Page => ({ path: '/', location: homeLocation, locationContext: { ...locationContext, location: homeLocation }, outlet: null });
  const current = (): Page => ({ path: location.pathname, location, locationContext, outlet });
  const [saved, setSaved] = useState(() => ({
    location, permissions, marketKey,
    pages: location.pathname === '/' ? [current()] : [home(), ...(canOpen(location.pathname) ? [current()] : [])],
  }));

  if (saved.location !== location || saved.permissions !== permissions || saved.marketKey !== marketKey) {
    const changedMarket = saved.marketKey !== marketKey;
    // Market-scoped pages reload; pages with independent filters retain their own state.
    const pages = saved.pages.filter((page) => canOpen(page.path)).map((page) => changedMarket && !routeFor(page.path)?.marketIndependent ? { ...page, outlet: null } : page);
    const index = pages.findIndex((page) => page.path === location.pathname);
    if (canOpen(location.pathname)) {
      if (index < 0) pages.push(current());
      else pages[index] = current();
    }
    setSaved({ location, permissions, marketKey, pages });
    if (changedMarket) updateDirty({});
  }

  const setDirty = useCallback((path: string, value: boolean) => {
    updateDirty((previous) => previous[path] === value ? previous : { ...previous, [path]: value });
  }, []);
  const askDiscard = useCallback(() => confirm({
    title: t('workspace.discardTitle'), description: t('workspace.discardBody'),
    confirmLabel: t('workspace.discard'), cancelLabel: t('workspace.keepEditing'),
  }), [confirm, t]);
  const hasDirty = Object.values(dirty).some(Boolean);
  useEffect(() => {
    if (!hasDirty) return;
    const guard = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [hasDirty]);

  const closing = useRef(false);
  const close = async (path: string) => {
    if (path === '/' || closing.current) return;
    closing.current = true;
    try {
      if (dirty[path] && !await askDiscard()) return;
      const index = saved.pages.findIndex((page) => page.path === path);
      if (index < 0) return;
      if (path === location.pathname) {
        const adjacent = saved.pages[index + 1] ?? saved.pages[index - 1];
        await navigate(adjacent ? url(adjacent.location) : '/', { replace: true });
      }
      setSaved((previous) => ({ ...previous, pages: previous.pages.filter((page) => page.path !== path) }));
      updateDirty((previous) => { const next = { ...previous }; delete next[path]; return next; });
    } finally {
      closing.current = false;
    }
  };
  const actions = useMemo(() => ({
    href: (path: string) => {
      const page = saved.pages.find((item) => item.path === path);
      return page ? url(page.location) : path;
    },
    confirmMarketChange: async () => !hasDirty || askDiscard(),
  }), [saved.pages, hasDirty, askDiscard]);

  return (
    <WorkspaceContext.Provider value={actions}>
      <PagesContext.Provider value={{ pages: saved.pages, active: location.pathname, marketKey, fallback: outlet, dirty, setDirty, close }}>
        {children}
      </PagesContext.Provider>
    </WorkspaceContext.Provider>
  );
}

/** A compact, scrollable workspace tab strip with a pinned home and keyboard navigation. */
export function WorkspaceTabs() {
  const workspace = useContext(PagesContext)!;
  const { t } = useTranslation('shell');
  const title = useNavTitle();
  const navigate = useNavigate();
  const activeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { activeRef.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }, [workspace.active]);
  const label = (page: Page) => {
    if (page.path === '/') return t('workspace.home');
    if (page.path === '/profile') return t('header.profile');
    const route = routeFor(page.path);
    const match = route && matchPath(route.path, page.path);
    const id = match ? Object.values(match.params).filter(Boolean).join(' · ') : '';
    return `${route ? title(route.title) : page.path}${id ? ` #${id}` : ''}`;
  };
  return (
    <div role="tablist" aria-label={t('workspace.label')} className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-subtle px-3 pt-2 sm:px-6">
      {workspace.pages.map((page, index) => {
        const selected = workspace.active === page.path;
        const name = label(page);
        return (
          <div key={page.path} role="presentation" className={cn('flex shrink-0 items-center rounded-t-md border border-b-2', selected ? 'border-line border-b-accent bg-surface' : 'border-transparent hover:bg-muted')}>
            <button
              ref={selected ? activeRef : undefined} type="button" role="tab" aria-selected={selected} aria-controls={`workspace-panel-${page.path}`} id={`workspace-tab-${page.path}`}
              tabIndex={selected ? 0 : -1} title={name}
              className={cn('flex h-9 max-w-64 items-center gap-2 rounded-t-md border-0 bg-transparent px-3 font-sans text-sm', selected ? 'font-bold text-accent' : 'text-ink-muted', focusRing)}
              onClick={() => void navigate(url(page.location))}
              onKeyDown={(event) => {
                const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
                const next = event.key === 'Home' ? 0 : event.key === 'End' ? workspace.pages.length - 1 : (index + offset + workspace.pages.length) % workspace.pages.length;
                if (offset || event.key === 'Home' || event.key === 'End') {
                  event.preventDefault();
                  void Promise.resolve(navigate(url(workspace.pages[next]!.location))).then(() => document.getElementById(`workspace-tab-${workspace.pages[next]!.path}`)?.focus());
                } else if (event.key === 'Delete' && page.path !== '/') {
                  event.preventDefault(); void workspace.close(page.path);
                }
              }}
            >
              {page.path === '/' && <House size={14} aria-hidden />}
              <span className="truncate">{name}</span>
              {workspace.dirty[page.path] && <span aria-label={t('workspace.unsaved')} className="size-1.5 shrink-0 rounded-full bg-warning" />}
            </button>
            {page.path !== '/' && <button type="button" aria-label={t('workspace.close', { name })} onClick={() => void workspace.close(page.path)} className={cn('mr-1 flex size-6 shrink-0 items-center justify-center rounded border-0 bg-transparent text-ink-muted hover:bg-muted hover:text-ink', focusRing)}><X size={13} aria-hidden /></button>}
          </div>
        );
      })}
    </div>
  );
}

/** Frozen locations keep inactive screens from reading another tab's filters or route params. */
function CachedPage({ page, active, setDirty }: { page: Page; active: boolean; setDirty(path: string, dirty: boolean): void }) {
  const scroll = useRef<HTMLDivElement>(null);
  const top = useRef(0);
  useLayoutEffect(() => { if (active && scroll.current) scroll.current.scrollTop = top.current; }, [active]);
  const report = useCallback((dirty: boolean) => setDirty(page.path, dirty), [page.path, setDirty]);
  return (
    <div ref={scroll} hidden={!active} role="tabpanel" id={`workspace-panel-${page.path}`} aria-labelledby={`workspace-tab-${page.path}`} tabIndex={0}
      onScroll={(event) => { if (active) top.current = event.currentTarget.scrollTop; }}
      className="relative min-h-0 flex-1 overflow-y-auto px-3 pb-24 pt-3 outline-none sm:px-6 sm:pt-5">
      <WorkspacePageContext.Provider value={report}>
        <UNSAFE_LocationContext.Provider value={page.locationContext}>
          <Activity mode={active ? 'visible' : 'hidden'}>
            <Suspense fallback={<PageSpinner />}>{page.outlet}</Suspense>
          </Activity>
        </UNSAFE_LocationContext.Provider>
      </WorkspacePageContext.Provider>
    </div>
  );
}

/** Hidden pages retain state while Activity pauses their effects, polling and shortcuts. */
export function WorkspacePages() {
  const workspace = useContext(PagesContext)!;
  return <>
    {workspace.pages.map((page) => <CachedPage key={`${routeFor(page.path)?.marketIndependent ? 'independent' : workspace.marketKey}:${page.path}`} page={page} active={workspace.active === page.path} setDirty={workspace.setDirty} />)}
    {!workspace.pages.some((page) => page.path === workspace.active) && <div className="min-h-0 flex-1 overflow-y-auto p-6"><Suspense fallback={<PageSpinner />}>{workspace.fallback}</Suspense></div>}
  </>;
}
